import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, Switch, TextField,
  Select, MenuItem, FormControl, InputLabel, Chip, Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { color } from '../theme/tokens';
import {
  PageHeader, Panel, DataTable, FormDialog, ConfirmDialog, useFeedback
} from '../components/ui';

const CARD_TYPES = [
  { value: 'credit', label: 'Credit Card' },
  { value: 'debit', label: 'Debit Card' },
  { value: 'cardless', label: 'Cardless / BNPL' },
];

const cleanRupee = (str) => typeof str === 'string' ? str.replace(/\?\s*(\d)/g, '₹$1') : str;

const OFFER_TYPES = [
  { value: 'instant_discount', label: 'Instant Discount' },
  { value: 'no_cost_emi', label: 'No-Cost EMI' },
  { value: 'standard_emi', label: 'Standard EMI' },
];

const initialForm = {
  bank_name: '',
  bank_code: '',
  card_type: 'credit',
  offer_title: '',
  offer_type: 'instant_discount',
  discount_amount: 0,
  discount_percent: 0,
  min_order_value: 15000,
  max_discount: 3000,
  no_cost_tenures: '3,6',
  standard_tenures: '9,12',
  interest_rate_pa: 14.0,
  badge_text: 'OFFER',
  terms: '',
  is_active: 1,
  sort_order: 1,
};

export default function BankOffers() {
  const { success, error } = useFeedback();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/bank-offers');
      setOffers(res.data?.data || []);
    } catch (err) {
      error('Failed to load bank offers');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData(initialForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (offer) => {
    setEditingId(offer.id);
    setFormData({
      bank_name: offer.bank_name || '',
      bank_code: offer.bank_code || '',
      card_type: offer.card_type || 'credit',
      offer_title: cleanRupee(offer.offer_title || ''),
      offer_type: offer.offer_type || 'instant_discount',
      discount_amount: Number(offer.discount_amount || 0),
      discount_percent: Number(offer.discount_percent || 0),
      min_order_value: Number(offer.min_order_value || 0),
      max_discount: Number(offer.max_discount || 0),
      no_cost_tenures: offer.no_cost_tenures || '',
      standard_tenures: offer.standard_tenures || '',
      interest_rate_pa: Number(offer.interest_rate_pa || 14.0),
      badge_text: cleanRupee(offer.badge_text || ''),
      terms: cleanRupee(offer.terms || ''),
      is_active: offer.is_active ? 1 : 0,
      sort_order: Number(offer.sort_order || 0),
    });
    setDialogOpen(true);
  };

  const handleToggleActive = async (id) => {
    try {
      await api.patch(`/api/admin/bank-offers/${id}/toggle`);
      setOffers((prev) =>
        prev.map((o) => (o.id === id ? { ...o, is_active: o.is_active ? 0 : 1 } : o))
      );
      success('Bank offer status updated');
    } catch {
      error('Failed to toggle status');
    }
  };

  const handleSave = async () => {
    if (!formData.bank_name || !formData.offer_title) {
      error('Bank name and offer title are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/admin/bank-offers/${editingId}`, formData);
        success('Bank offer updated');
      } else {
        await api.post('/api/admin/bank-offers', formData);
        success('Bank offer created');
      }
      setDialogOpen(false);
      fetchOffers();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to save offer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/api/admin/bank-offers/${deletingId}`);
      success('Bank offer deleted');
      setDeleteConfirmOpen(false);
      setDeletingId(null);
      fetchOffers();
    } catch {
      error('Failed to delete offer');
    }
  };

  const columns = [
    {
      key: 'bank_name',
      label: 'Bank & Card',
      render: (_v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '8px', background: color?.surfaceAlt || '#F1F5F9',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: color?.ink?.[700] || '#374151',
            fontWeight: 800, fontSize: '0.75rem'
          }}>
            {row.bank_code || 'BANK'}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: color?.ink?.[900] || '#111827' }}>
              {row.bank_name}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: color?.ink?.[500] || '#6B7280', textTransform: 'capitalize' }}>
              {row.card_type} Card
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'offer_title',
      label: 'Offer Headline',
      render: (_v, row) => (
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: color?.ink?.[800] || '#1F2937' }}>
            {cleanRupee(row.offer_title)}
          </Typography>
          {row.badge_text && (
            <Chip size="small" label={cleanRupee(row.badge_text)} sx={{ height: 18, fontSize: '0.65rem', mt: 0.5, fontWeight: 700 }} color="primary" />
          )}
        </Box>
      ),
    },
    {
      key: 'discount_amount',
      label: 'Discount / Min. Order',
      render: (_v, row) => (
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: color?.emerald?.[700] || '#047857' }}>
            {row.discount_amount > 0 ? `₹${Number(row.discount_amount).toLocaleString('en-IN')} Flat` : `${row.discount_percent}% Off`}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: color?.ink?.[500] || '#6B7280' }}>
            Min: ₹{Number(row.min_order_value).toLocaleString('en-IN')}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'no_cost_tenures',
      label: 'No-Cost Tenures',
      render: (v) => (
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
          {v ? `${v} Months` : 'None'}
        </Typography>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      align: 'center',
      render: (v, row) => (
        <Switch
          checked={Boolean(v)}
          onChange={() => handleToggleActive(row.id)}
          size="small"
          color="primary"
        />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (_v, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Edit Offer">
            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => { setDeletingId(row.id); setDeleteConfirmOpen(true); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Bank Offers & EMI Management"
        subtitle="Configure dynamic instant card discounts, No-Cost EMI subsidies, and bank tenures streamed to storefront PDP and Checkout."
        action={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={fetchOffers}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
            >
              Add Bank Offer
            </Button>
          </Stack>
        }
      />

      <Panel sx={{ mt: 3 }}>
        <DataTable
          columns={columns}
          rows={offers}
          loading={loading}
          rowKey="id"
          emptyTitle="No bank offers configured"
          emptyMessage="Click 'Add Bank Offer' above to configure your first bank discount or No-Cost EMI plan."
        />
      </Panel>

      {/* Add / Edit Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingId ? 'Edit Bank Offer' : 'Add New Bank Offer'}
        submitLabel={editingId ? 'Save Changes' : 'Create Offer'}
        onSubmit={handleSave}
        loading={saving}
        saving={saving}
        maxWidth="sm"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Bank Name"
              required
              fullWidth
              size="small"
              placeholder="e.g. HDFC Bank"
              value={formData.bank_name}
              onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
            />
            <TextField
              label="Bank Code"
              size="small"
              placeholder="e.g. HDFC"
              sx={{ width: 140 }}
              value={formData.bank_code}
              onChange={(e) => setFormData({ ...formData, bank_code: e.target.value })}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Card Type</InputLabel>
              <Select
                value={formData.card_type}
                label="Card Type"
                onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
              >
                {CARD_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Offer Type</InputLabel>
              <Select
                value={formData.offer_type}
                label="Offer Type"
                onChange={(e) => setFormData({ ...formData, offer_type: e.target.value })}
              >
                {OFFER_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Offer Headline / Title"
            required
            fullWidth
            size="small"
            placeholder="e.g. Flat ₹3,000 Instant Discount on Credit Cards & EasyEMI"
            value={formData.offer_title}
            onChange={(e) => setFormData({ ...formData, offer_title: e.target.value })}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Discount Amount (₹)"
              type="number"
              size="small"
              fullWidth
              value={formData.discount_amount}
              onChange={(e) => setFormData({ ...formData, discount_amount: Number(e.target.value) })}
            />
            <TextField
              label="Min. Order Value (₹)"
              type="number"
              size="small"
              fullWidth
              value={formData.min_order_value}
              onChange={(e) => setFormData({ ...formData, min_order_value: Number(e.target.value) })}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="No-Cost Tenures (Months)"
              size="small"
              fullWidth
              placeholder="e.g. 3,6"
              helperText="Comma separated (e.g. 3,6)"
              value={formData.no_cost_tenures}
              onChange={(e) => setFormData({ ...formData, no_cost_tenures: e.target.value })}
            />
            <TextField
              label="Standard Tenures (Months)"
              size="small"
              fullWidth
              placeholder="e.g. 9,12,18"
              helperText="Comma separated (e.g. 9,12)"
              value={formData.standard_tenures}
              onChange={(e) => setFormData({ ...formData, standard_tenures: e.target.value })}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Interest Rate (% p.a.)"
              type="number"
              size="small"
              sx={{ width: 180 }}
              value={formData.interest_rate_pa}
              onChange={(e) => setFormData({ ...formData, interest_rate_pa: Number(e.target.value) })}
            />
            <TextField
              label="Badge Tag"
              size="small"
              fullWidth
              placeholder="e.g. FLAT ₹3,000 OFF"
              value={formData.badge_text}
              onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
            />
          </Stack>

          <TextField
            label="Offer Terms & Eligibility"
            multiline
            rows={2}
            size="small"
            fullWidth
            placeholder="e.g. Valid on transactions above ₹25,000 using Credit Cards."
            value={formData.terms}
            onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
          />
        </Box>
      </FormDialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Bank Offer?"
        message="Are you sure you want to remove this bank offer? It will no longer appear on product pages or in the EMI calculator."
        confirmLabel="Delete Offer"
        confirmColor="error"
      />
    </Box>
  );
}
